import { InteractionRequiredAuthError, LogLevel, PublicClientApplication } from '@azure/msal-browser'
import * as Sentry from '@sentry/vue'

const msalConfig = {
  auth: {
    clientId: process.env.VUE_APP_AAD_CLIENT_ID,
    authority: process.env.VUE_APP_AAD_AUTHORITY,
    knownAuthorities: [process.env.VUE_APP_AAD_KNOWN_AUTHORITY],
    redirectUri: process.env.VUE_APP_AAD_REDIRECT_URI, // Must be registered as a SPA redirectURI on your app registration
    postLogoutRedirectUri: process.env.VUE_APP_AAD_POST_LOGOUT_REDIRECT_URI, // Must be registered as a SPA redirectURI on your app registration
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
    secureCookies: false,
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Verbose,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            Sentry.captureException(message)
            console.error(message)
            return
          case LogLevel.Info:
            Sentry.captureMessage(message)
            return
          case LogLevel.Warning:
            Sentry.captureMessage(message)
            return
        }
      },
      piiLoggingEnabled: false,
    },
  },
}

const publicClientApplication = new PublicClientApplication(msalConfig)

const makeAccesstokenRequest = () => {
  const account = publicClientApplication.getAllAccounts()[0]
  return {
    account,
    forceRefresh: false,
    scopes: [process.env.VUE_APP_AAD_SCOPE],
  }
}

export const loginWithAdRedirect = async () => {
  try {
    const response = await publicClientApplication.handleRedirectPromise()
    if (response !== null) {
      return response.account
    } else {
      return await publicClientApplication.loginRedirect(makeAccesstokenRequest())
    }
  } catch (error) {
    Sentry.captureException(error)
    console.debug(error)
    return false
  }
}

// This uses the silent refresh mechanism to get a token to be used by the api.
// The silent refresh is caching and validating the tokens lifetime for us.
//
// Based on this example [as of 2023-06-10]:
// https://learn.microsoft.com/en-us/azure/active-directory/develop/scenario-spa-acquire-token?tabs=javascript2#acquire-a-token-with-a-redirect
export const getAdTokenRedirect = async () => {
  try {
    const redirectResponse = await publicClientApplication.handleRedirectPromise()
    if (redirectResponse !== null) {
      // Acquiring token silently successful, return access token
      return redirectResponse.accessToken
    } else {
      const accessTokenRequest = makeAccesstokenRequest()
      // It only works if there is a user account already present
      if (accessTokenRequest.account == null) return null
      try {
        return (await publicClientApplication.acquireTokenSilent(accessTokenRequest)).accessToken
      } catch (error) {
        console.warn('Silent token acquisition failed. Using redirect interaction to acquire token.')
        if (error instanceof InteractionRequiredAuthError) {
          return await publicClientApplication.acquireTokenRedirect(makeAccesstokenRequest())
        } else {
          Sentry.captureException(error)
          console.debug(error)
          return null
        }
      }
    }
  } catch (error) {
    Sentry.captureException(error)
    console.debug(error)
    return null
  }
}

export const adLogout = async () => {
  const accessTokenRequest = makeAccesstokenRequest()
  // can't if there is no account
  if (accessTokenRequest.account == null) return null

  return await publicClientApplication.logoutRedirect(msalConfig)
}
